<script setup lang="ts">
import { computed, ref, useAttrs, useSlots } from 'vue';
import { useSubscriptionStore } from '@organisms/ubiq-administration/store/subscriptions';
import { trans } from '../../../../_00_elements/translation';

const slots = useSlots();

const subscriptionStore = useSubscriptionStore();
const { getError, hasError } = subscriptionStore;

type Model = string | number | null;

const props = withDefaults(defineProps<{
  modelValue?: Model;
  type?: string;
  placeholder?: string;
  name: string;
  id?: string;
  label: string;
}>(), {
  modelValue: undefined,
  type: 'text',
  placeholder: '',
  id: '',
  label: '',
});

const emit = defineEmits<{(e: 'update:modelValue', value: Model): void;
  (e: 'blur', value: object): void;
}>();

const attrs = useAttrs();

const uid = computed<string>(() => props.id || props.name);
const fieldHasError = computed<boolean>(() => hasError(props.name));
const labelText = computed<string>(() => trans(props.label));
const fieldWasTouched = ref<boolean>(false);
const hasSlot = (slot: string): boolean => !!slots[slot];

const onInput = (e: Event): void => {
  fieldWasTouched.value = true;
  const target = e.target as HTMLInputElement | null;
  emit('update:modelValue', target?.value ?? '');
};

const onBlur = (): void => emit('blur', { name: props.name, value: props.modelValue, isTouched: fieldWasTouched });
</script>

<template>
  <div class="infos-customer__form-group">
    <label :for="uid" class="infos-customer__label">
      {{ labelText }}
    </label>

    <template v-if="hasSlot('input')">
      <slot name="input" />
    </template>
    <template v-else>
      <input
        :id="uid"
        :name="name"
        :type="type"
        :value="modelValue"
        :placeholder="placeholder"
        class="infos-customer__input"
        :class="{ error: fieldHasError }"
        v-bind="attrs"
        @input="onInput"
        @blur="onBlur"
      >
      <span
        v-if="fieldHasError"
        :id="`${uid}-error`"
        class="error-message"
      >
        {{ getError(name) }}
      </span>
    </template>
  </div>
</template>
